import React, { useEffect, useState } from 'react';
import { Action, redirect } from 'redux-first-router';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'model/State';
import { fetchTaxRate } from 'modules/data/taxRates/actions';

import { getCountryByCountryName, getStateOptionFromStates } from './common/utils';
import MACPageHeader from './components/macPageHeader';
import PaymentDetailsInfoSection from './components/PaymentDetailsInfoSection';
import { HeaderContainer, PageTitle } from './components/styledComponents';
import { UpdatePaymentDetailsForm } from './components/UpdatePaymentDetailsForm';
import { defaultFormState } from './constants/constants';
import {
  getCurrentMACContactEmail,
  selectCountryByName,
  selectDetails,
  selectDetailsProgress,
  selectGetCustomerByEmail,
  selectIsOnBehalfOfEmail,
  selectIsSuspended,
  selectUpdatedCCDetailsById,
  selectValidateCountryChange,
} from './duck/selectors';
import { fetchCountryByName } from './duck/actions/country';
import { route } from './duck/actions/landing';
import {
  postActivateSuspended,
  postReconcilableValidationResultAccounts,
} from './duck/actions/accountId';
import { fetchExternal, fetchValidateCountryChange } from './duck/actions/contact';
import { postNoteAccount } from './duck/actions/noteAccount';
import { MACCreditCardDetails } from './duck/api/storage';
import { checkFeatureGate, FeatureFlags } from 'util/statsig';
import { CloudProductKeys } from 'data/products/keys';
import { getTaxExemptionNoByCountryState, getTaxExemptions } from '../utils';

type SelectOption = {
  label: string;
  value: string;
};

const PaymentDetailsPage = () => {
  const MACCloudReadModeFG = checkFeatureGate(FeatureFlags.CART_MAC_CLOUD_READ_MODE);

  const dispatch = useDispatch();

  const [billingDetailsForm, setBillingDetailsForm] = useState(defaultFormState);
  const [states, setStates] = useState<SelectOption[]>([]);
  const [isCountrySelectChanged, setIsCountrySelectChanged] = useState(false);
  const [shouldShowPaymentDetailsForm, setShouldShowPaymentDetailsForm] = useState(false);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const accountId = useSelector((state: State) => state.location.payload.accountId);
  const contactEmail = useSelector(getCurrentMACContactEmail) || '';
  const accountDetails = useSelector((state: State) => selectDetails(state, accountId));
  const accountDetailsProgress = useSelector((state: State) =>
    selectDetailsProgress(state, accountId),
  );
  const customerDetails = useSelector(selectGetCustomerByEmail);
  const isAccountSuspended = useSelector(selectIsSuspended);
  const allCountries = useSelector((state: State) => state.data.countries.all?.result);
  const countryDetails = useSelector((state: State) =>
    selectCountryByName(state, billingDetailsForm.country.label),
  );
  const postedCreditCardDetails = useSelector((state) =>
    selectUpdatedCCDetailsById(accountId, state as State),
  );
  const selectValidateCountry = useSelector((state: State) =>
    selectValidateCountryChange(state, billingDetailsForm.country.value),
  );
  const partnerProfile: any = useSelector(selectIsOnBehalfOfEmail);

  useEffect(() => {
    document.title = 'My Atlassian | Update Payment Details';
  }, [
    accountId,
    accountDetails,
    contactEmail,
    customerDetails,
    isAccountSuspended,
    allCountries,
    countryDetails,
    postedCreditCardDetails,
    selectValidateCountry,
  ]);

  useEffect(() => {
    if (accountDetailsProgress?.isCompleted && !accountDetails) {
      dispatch(redirect(route() as Action));
    }
  }, [accountDetailsProgress]);

  useEffect(() => {
    let canUserSeeThePage = false;
    if (accountDetails?.contactRelationships) {
      canUserSeeThePage = accountDetails?.contactRelationships?.some(
        ({ contact }) => contact.contactDetails?.email === contactEmail,
      );
    }

    if (accountDetails && !canUserSeeThePage) {
      dispatch(redirect(route() as Action));
    }
  }, [accountDetails, contactEmail]);

  useEffect(() => {
    if (accountDetails && partnerProfile) {
      if (accountDetails.partnerManaged && !partnerProfile.contactPartnerForAccount) {
        dispatch(redirect(route() as Action));
      }
    }
  }, [accountDetails, partnerProfile]);

  useEffect(() => {
    if (accountDetails) {
      const productPackage =
        accountDetails.merchandise?.productPackage || accountDetails.productPackage;
      if (
        productPackage?.hostingType === 'HOSTED' &&
        productPackage.key !== CloudProductKeys.BITBUCKET &&
        MACCloudReadModeFG
      ) {
        dispatch(redirect(route() as Action));
      }
    }
  }, [accountDetails, MACCloudReadModeFG]);

  useEffect(() => {
    if (billingDetailsForm.state?.value) {
      dispatch(fetchTaxRate(billingDetailsForm.country.value, billingDetailsForm.state.value));
    }
  }, [billingDetailsForm.state?.value]);

  useEffect(() => {
    if (billingDetailsForm.country.label && isCountrySelectChanged) {
      dispatch(fetchCountryByName(billingDetailsForm.country.label));
    }
  }, [billingDetailsForm.country.value, isCountrySelectChanged]);

  useEffect(() => {
    if (customerDetails[contactEmail]?.result) {
      dispatch(
        fetchCountryByName(customerDetails[contactEmail]?.result?.organisationDetails?.country),
      );
    }
  }, [customerDetails[contactEmail]]);

  useEffect(() => {
    if (customerDetails?.[contactEmail]?.result && allCountries) {
      const {
        contactDetails: { firstName, lastName },
        organisationDetails: {
          name: companyName,
          address1,
          address2,
          city,
          country,
          postcode,
          taxId,
          payingVatResponsible: vatResponsible,
          state,
          organisationTaxExemptions,
        },
      } = customerDetails[contactEmail].result;
      const countryObj = getCountryByCountryName(allCountries, country) || '';
      setBillingDetailsForm({
        firstName,
        lastName,
        companyName,
        address1,
        address2,
        city,
        country: countryObj,
        state: { label: '', value: '' },
        postcode,
        taxId,
        vatResponsible,
        taxExemptions: getTaxExemptionNoByCountryState(
          countryObj.value,
          taxId,
          state,
          organisationTaxExemptions,
        ),
      });
    }
  }, [customerDetails, contactEmail, allCountries]);

  useEffect(() => {
    if (
      customerDetails?.[contactEmail]?.result &&
      countryDetails &&
      states.length &&
      !isCountrySelectChanged
    ) {
      setBillingDetailsForm({
        ...billingDetailsForm,
        state: getStateOptionFromStates(
          states,
          customerDetails[contactEmail].result.organisationDetails.state,
        ),
      });
      dispatch(fetchValidateCountryChange(billingDetailsForm.country.value));
    }
  }, [customerDetails, contactEmail, countryDetails, states.length, isCountrySelectChanged]);

  useEffect(() => {
    if (countryDetails?.states.length && billingDetailsForm.country) {
      setStates(countryDetails.states.map(({ name, code }) => ({ label: name, value: code })));
    }
  }, [countryDetails, billingDetailsForm.country.value]);

  useEffect(() => {
    if (accountDetails && contactEmail && customerDetails?.[contactEmail]?.result) {
      const mainTechAndBillContacts = accountDetails.contactRelationships?.filter(
        ({ main, contact }) =>
          main &&
          contact?.contactDetails?.externalUuid ===
            customerDetails?.[contactEmail]?.result?.contactDetails?.externalUuid,
      );
      if (mainTechAndBillContacts?.length === 2) {
        setShouldShowPaymentDetailsForm(true);
      }
      setEndDate(new Date(accountDetails.latestPeriod?.endDate));
    }
  }, [accountDetails, contactEmail, customerDetails?.[contactEmail]?.result]);

  useEffect(() => {
    if (postedCreditCardDetails === '' && selectValidateCountry) {
      const currentContactDetails = customerDetails[contactEmail].result;
      const {
        type,
        maskedNumber,
        expiryMonth,
        expiryYear,
        name: ccName,
      } = MACCreditCardDetails().load();
      const noteAccountPayload = {
        author: `${billingDetailsForm.firstName} ${billingDetailsForm.lastName} OR ${currentContactDetails.contactDetails.displayName}`,
        body: `The payment method has been updated by end user - ${type} - ${maskedNumber} (${expiryMonth}/${expiryYear}) - ${ccName}`,
      };

      const updatedContact = {
        contactDetails: {
          ...currentContactDetails.contactDetails,
          firstName: billingDetailsForm.firstName,
          lastName: billingDetailsForm.lastName,
        },
        organisationDetails: {
          ...currentContactDetails.organisationDetails,
          name: billingDetailsForm.companyName,

          address1: billingDetailsForm.address1,
          address2: billingDetailsForm.address2,
          city: billingDetailsForm.city,
          country: billingDetailsForm.country.label,
          state: billingDetailsForm.state.value,
          postcode: billingDetailsForm.postcode,
          taxId: billingDetailsForm.taxId,
          payingVatResponsible: billingDetailsForm.vatResponsible,
          organisationTaxExemptions: getTaxExemptions(
            billingDetailsForm.country.value,
            billingDetailsForm.taxId,
            billingDetailsForm.state.value,
            billingDetailsForm.taxExemptions,
          ),
        },
      };

      if (shouldShowPaymentDetailsForm) {
        dispatch(fetchExternal(updatedContact));
      }
      dispatch(postReconcilableValidationResultAccounts(selectValidateCountry));
      if (isAccountSuspended) {
        dispatch(postActivateSuspended(accountId));
      }
      dispatch(postNoteAccount(accountId, noteAccountPayload));
      dispatch(redirect(route() as Action));
    }
  }, [postedCreditCardDetails, selectValidateCountry]);

  return (
    <>
      <MACPageHeader />
      <HeaderContainer bottomLine flexDirectionColumn>
        <PageTitle>
          <FormattedMessage
            defaultMessage="Update Payment Details"
            id="mac.payment-details-page.header"
          />
        </PageTitle>
        <p>
          <FormattedMessage
            defaultMessage="You can use this form to update the payment details associated with your account."
            id="mac.payment-details-page.content"
          />
        </p>
      </HeaderContainer>
      <PaymentDetailsInfoSection />
      <UpdatePaymentDetailsForm
        accountId={accountId}
        endDate={endDate}
        shouldShowPaymentDetailsForm={shouldShowPaymentDetailsForm}
        countryDetails={countryDetails}
        allCountries={allCountries}
        states={states}
        billingDetailsForm={billingDetailsForm}
        customerDetailsForContact={customerDetails?.[contactEmail]?.result}
        contactEmail={contactEmail}
        setBillingDetailsForm={setBillingDetailsForm}
        setIsCountrySelectChanged={setIsCountrySelectChanged}
        selectValidateCountry={selectValidateCountry}
        productPackage={accountDetails?.merchandise?.productPackage}
      />
    </>
  );
};

export default PaymentDetailsPage;
